/**
 * rother3 companion — reading a ZIP of exported audits.
 *
 * ## Why this exists
 *
 * An owner comparing 100+ businesses has 100+ export files. Hand-selecting 100 files in a
 * file dialog is not a workflow, it is a chore, and a chore that looks like the tool is broken
 * is worse than no bulk import at all. So the import accepts a ZIP: the one container a person
 * can produce by selecting a folder and pressing "Compress".
 *
 * ## Why no library
 *
 * A ZIP reader is ~150 lines on top of what the browser already ships. `DecompressionStream`
 * has been in Chrome since 80, Firefox since 113 and Safari since 16.4 (March 2023), which
 * covers every browser this extension targets. Vendoring `fflate` or `jszip` instead would add
 * a supply-chain surface and ~30 KB to a project whose stated constraints include "no budget"
 * and "reproducible", to avoid writing a directory-scanning loop.
 *
 * ## What this does and does not do
 *
 * It reads. It never writes, never extracts to disk, and never follows a path outside the
 * archive — there is no filesystem here at all, so zip-slip does not exist as a threat. A
 * malformed archive produces a named error, never a partially-imported silent state.
 */

/**
 * Why each limit, so a future reader can tell which ones are load-bearing.
 *
 * `MAX_ENTRIES` is the one that matters. A 42-byte "zip bomb" — 30,000 entries all named
 * `.json` — is ~3 MB on the wire and would be 30,000 IndexedDB writes, which locks the tab for
 * minutes. `MAX_TOTAL_BYTES` is the other: a small archive can expand to gigabytes of JSON.
 */
export const ZIP_LIMITS = {
  /** Entries examined in one archive. */
  maxEntries: 2000,
  /** Sum of *uncompressed* bytes across all entries read from one archive. */
  maxTotalBytes: 256 * 1024 * 1024,
  /** One entry's uncompressed size. */
  maxEntryBytes: 32 * 1024 * 1024,
};

/** Compression methods in the ZIP spec. Everything else is refused rather than guessed at. */
const METHOD = { store: 0, deflate: 8 };

/** The four bytes every ZIP ends with, searched backwards from the end. */
const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
/** ZIP64 end-of-central-directory locator, which immediately precedes the EOCD. */
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50;

/**
 * Find the End Of Central Directory record.
 *
 * ## Why it is searched for, not assumed
 *
 * The EOCD sits at the very end, but its 16-bit comment-length field means up to 64 KB of
 * trailing comment can follow it, and self-extracting archives prepend a stub. Assuming it is
 * the last 22 bytes is the standard bug, and it surfaces as "that file is not a zip" on an
 * archive produced by a perfectly ordinary tool. So the last 64 KB + 22 bytes is scanned
 * backwards for the signature.
 */
function findEndOfCentralDirectory(view, byteLength) {
  const searchLength = Math.min(byteLength, 22 + 0xffff);
  const start = byteLength - searchLength;
  for (let offset = byteLength - 22; offset >= start; offset -= 1) {
    if (view.getUint32(offset, true) !== EOCD_SIGNATURE) {continue;}
    return {
      entries: view.getUint16(offset + 10, true),
      centralDirectorySize: view.getUint32(offset + 12, true),
      centralDirectoryOffset: view.getUint32(offset + 16, true),
    };
  }
  return null;
}

/**
 * One entry from the central directory.
 *
 * The central directory is authoritative, not the local header: a hand-crafted archive can
 * disagree between the two, and the central directory is what every other reader trusts.
 */
function readCentralEntry(view, offset) {
  if (view.getUint32(offset, true) !== CENTRAL_SIGNATURE) {return null;}

  const flags = view.getUint16(offset + 8, true);
  const method = view.getUint16(offset + 10, true);
  const compressedSize = view.getUint32(offset + 20, true);
  const uncompressedSize = view.getUint32(offset + 24, true);
  const nameLength = view.getUint16(offset + 28, true);
  const extraLength = view.getUint16(offset + 30, true);
  const commentLength = view.getUint16(offset + 32, true);
  const localHeaderOffset = view.getUint32(offset + 42, true);
  const nameBytes = new Uint8Array(view.buffer, view.byteOffset + offset + 46, nameLength);

  return {
    flags,
    method,
    compressedSize,
    uncompressedSize,
    localHeaderOffset,
    /**
     * Name length in **bytes**, not characters. A decoded name's `.length` is its character
     * count, which is fewer than its byte count for any non-ASCII filename, and advancing the
     * cursor by the character count desynchronises every record after the first accented
     * character. Decoding happens for display; arithmetic happens on bytes.
     */
    nameByteLength: nameLength,
    extraLength,
    commentLength,
    name: new TextDecoder('utf-8').decode(nameBytes),
  };
}

/**
 * The ZIP64 offset overrides, if the archive needs them.
 *
 * A ZIP64 entry stores its real sizes as 0xFFFFFFFF and its real offsets in an "extra field"
 * (header id 0x0001). An archive of 100 JSON exports will never need this — four gigabytes and
 * 65,535 entries are the thresholds — so this returns `null` rather than growing the reader
 * for a case that cannot occur at this project's scale. A ZIP64 archive is **refused with a
 * message**, not misread.
 */
function zip64Unsupported(entry) {
  return entry.compressedSize === 0xffffffff || entry.uncompressedSize === 0xffffffff ||
    entry.localHeaderOffset === 0xffffffff;
}

/**
 * Decompress one entry's bytes.
 *
 * `method === 0` (stored) returns the slice untouched. `method === 8` (deflate) goes through
 * the browser's own inflater. Anything else — bzip2, LZMA, XZ, AES — is refused by name,
 * because silently returning the raw compressed bytes would produce JSON.parse's
 * "Unexpected token" on a payload the user could see was a real file.
 */
async function decompressEntry(raw, method, name) {
  if (method === METHOD.store) {return raw;}

  if (method !== METHOD.deflate) {
    const label =
      method === 12 ? 'bzip2' :
      method === 14 ? 'LZMA' :
      method === 93 ? 'zstd' :
      method === 99 ? 'AES-encrypted' :
      `method ${method}`;
    throw new Error(
      `"${name}" is compressed with ${label}, which this browser cannot read. ` +
        'Re-create the archive with "Store" or "Deflate" compression.'
    );
  }

  if (typeof DecompressionStream === 'undefined') {
    throw new Error(
      'This browser cannot unzip files (needs Chrome 80+, Firefox 113+, or Safari 16.4+). ' +
        'Import the .json files directly instead.'
    );
  }

  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  const parts = await new Response(stream).arrayBuffer();
  return new Uint8Array(parts);
}

/**
 * The bytes of one entry, located via its local header.
 *
 * The local header repeats the name and extra-field lengths, and they **may differ** from the
 * central directory's — that is legal, and a reader that assumes they match seeks to the wrong
 * offset and returns a slice of garbage. So the local header is read for its own lengths.
 */
async function readEntryBytes(view, byteLength, entry) {
  const headerOffset = entry.localHeaderOffset;
  if (headerOffset + 30 > byteLength || view.getUint32(headerOffset, true) !== LOCAL_SIGNATURE) {
    throw new Error(`"${entry.name}" has a damaged header.`);
  }

  const nameLength = view.getUint16(headerOffset + 26, true);
  const extraLength = view.getUint16(headerOffset + 28, true);
  const dataOffset = headerOffset + 30 + nameLength + extraLength;

  if (entry.compressedSize === 0) {
    // A directory entry, or a legitimately empty file. Both are "no data", not an error.
    return new Uint8Array(0);
  }
  if (dataOffset + entry.compressedSize > byteLength) {
    throw new Error(`"${entry.name}" is truncated — the archive ends early.`);
  }

  return decompressEntry(
    new Uint8Array(view.buffer, view.byteOffset + dataOffset, entry.compressedSize),
    entry.method,
    entry.name
  );
}

/**
 * Is this filename a JSON document?
 *
 * Case-insensitive, and it also accepts `.jsonl`-style double extensions such as
 * `audit.json.zip` because the *outer* `.zip` is stripped before this is ever called. Directories
 * end in `/` and are skipped here.
 */
function isJsonName(name) {
  return !name.endsWith('/') && /\.json$/i.test(name);
}

/** macOS resource-fork junk and Windows thumbnails that ride along in a zipped folder. */
function isJunkName(name) {
  return name.startsWith('__MACOSX/') || name.endsWith('/.DS_Store') || name === '.DS_Store';
}

/**
 * Every `.json` file inside a ZIP, decoded as text.
 *
 * ## What is skipped, and stated rather than silently dropped
 *
 * - Directories, `__MACOSX/`, `.DS_Store` — never data.
 * - Non-JSON entries. A zipped folder routinely contains `.md` notes and `.png` screenshots.
 * - The **ZIP64 refusal**, which throws rather than returning a partial list.
 *
 * ## Rejects
 *
 * - Not a ZIP (no EOCD) → named error, so "choose a .zip file" is not what the user reads.
 * - Encrypted (`general purpose bit 0`) → named error. The browser has no unzip password, and
 *   guessing would be worse than saying so.
 * - Over the entry/size limits → named error carrying the number, because "too many files"
 *   without the count is unactionable.
 *
 * @param {ArrayBuffer|Uint8Array} buffer
 * @param {{limits?: Partial<typeof ZIP_LIMITS>}} [options]
 * @returns {Promise<{name: string, text: string, bytes: number}[]>}
 */
export async function readZipJsonFiles(buffer, options = {}) {
  const limits = { ...ZIP_LIMITS, ...options.limits };
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const byteLength = bytes.byteLength;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // A ZIP's smallest valid member is 22 bytes (an empty archive). Below that, no amount of
  // scanning will find a signature, so the message can be definitive rather than "not found".
  if (byteLength < 22) {
    throw new Error('That file is too small to be a ZIP archive.');
  }

  const eocd = findEndOfCentralDirectory(view, byteLength);
  if (eocd === null) {
    throw new Error(
      'That file is not a ZIP archive. If it is a single audit, choose the .json file itself.'
    );
  }

  if (byteLength >= 20 && view.getUint32(byteLength - 20, true) === ZIP64_LOCATOR_SIGNATURE) {
    throw new Error(
      'That ZIP uses the ZIP64 format, which is only for archives over 4 GB or 65,535 files. ' +
        'Re-create it as a standard ZIP.'
    );
  }

  if (eocd.entries > limits.maxEntries) {
    throw new Error(
      `That archive holds ${eocd.entries.toLocaleString('en')} entries, and this page reads at ` +
        `most ${limits.maxEntries.toLocaleString('en')} per archive. Split it into smaller archives.`
    );
  }

  const results = [];
  let offset = eocd.centralDirectoryOffset;
  let totalBytes = 0;
  let skippedNonJson = 0;

  for (let index = 0; index < eocd.entries; index += 1) {
    if (offset + 46 > byteLength) {
      throw new Error('That archive\'s central directory is truncated.');
    }
    const entry = readCentralEntry(view, offset);
    if (entry === null) {
      throw new Error('That archive\'s central directory is damaged.');
    }

    // Advance first, before any `continue`. Skipping the advance on a filtered-out record is
    // how a loop ends up parsing the tail of one header as the next header.
    offset += 46 + entry.nameByteLength + entry.extraLength + entry.commentLength;

    if (entry.flags & 0x0001) {
      throw new Error(
        `"${entry.name}" is password-protected. Remove the password from the archive and try again.`
      );
    }
    if (isJunkName(entry.name) || !isJsonName(entry.name)) {
      if (!isJunkName(entry.name)) {skippedNonJson += 1;}
      continue;
    }
    if (zip64Unsupported(entry)) {
      throw new Error(`"${entry.name}" uses ZIP64 offsets, which this reader does not support.`);
    }
    if (entry.uncompressedSize > limits.maxEntryBytes) {
      throw new Error(
        `"${entry.name}" is ${Math.round(entry.uncompressedSize / 1024 / 1024)} MB, over the ` +
          `${Math.round(limits.maxEntryBytes / 1024 / 1024)} MB per-file limit.`
      );
    }

    totalBytes += entry.uncompressedSize;
    if (totalBytes > limits.maxTotalBytes) {
      throw new Error(
        'That archive expands to more than ' +
          `${Math.round(limits.maxTotalBytes / 1024 / 1024)} MB. Split it into smaller archives.`
      );
    }

    const raw = await readEntryBytes(view, byteLength, entry);
    results.push({
      name: entry.name,
      text: new TextDecoder('utf-8').decode(raw),
      bytes: raw.byteLength,
    });
  }

  if (results.length === 0) {
    throw new Error(
      skippedNonJson > 0
        ? `That ZIP has no .json files in it — it holds ${skippedNonJson} other file(s).`
        : 'That ZIP is empty.'
    );
  }

  return results;
}